import React from 'react';
import { ShieldCheck, Lock, Server, KeyRound, FileCheck2, Eye, Globe2, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const PILLARS = [
  { icon: Server, title: 'Data sovereignty', body: '100% hosted in Australia (AWS Sydney, ap-southeast-2). No data leaves the country at rest.' },
  { icon: Lock, title: 'Encryption', body: 'TLS 1.3 in transit. AES-256 at rest. Field-level encryption for sensitive PII (passport, MRZ, medical).' },
  { icon: KeyRound, title: 'Access control', body: 'MFA mandatory for staff. Fine-grained RBAC across seven roles. Immutable audit log for every admin action.' },
  { icon: FileCheck2, title: 'Compliance', body: 'ISO 27001:2022 controls. OMARA Code aligned. NSW Law Society trust. Australian Privacy Principles + NDB scheme.' },
  { icon: Eye, title: 'Auditability', body: 'Append-only event log for financial and lodgement actions. 7-year retention. Auditor-ready exports.' },
  { icon: AlertTriangle, title: 'Breach preparedness', body: 'Detection, playbook, and 72-hour Notifiable Data Breach workflow baked in. Quarterly pen-tests.' },
];

export default function SecurityPage() {
  return (
    <>
      <section className="border-b border-slate-200 bg-[#1A3A5C] text-white">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="secondary" className="bg-[#2ECC9A]/10 text-[#2ECC9A] border-[#2ECC9A]/30">
              Security & compliance
            </Badge>
            <h1 className="mt-4 font-serif text-5xl font-bold leading-tight tracking-tight">
              Built to survive the audit before it&rsquo;s scheduled.
            </h1>
            <p className="mt-4 text-lg text-white/70">
              You handle passports, medicals, and money you don&rsquo;t own. We architected ImmiPro the way we&rsquo;d architect our own practice.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid gap-5 md:grid-cols-3">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <Card key={p.title} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#2ECC9A]/10">
                    <Icon className="h-5 w-5 text-[#2ECC9A]" />
                  </div>
                  <div className="mt-4 text-lg font-semibold text-[#1A3A5C]">{p.title}</div>
                  <div className="mt-1 text-sm text-slate-600">{p.body}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-16 rounded-xl border border-slate-200 bg-white p-8">
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-[#2ECC9A]" />
                <div className="text-xs font-semibold uppercase tracking-widest text-[#2ECC9A]">Compliance map</div>
              </div>
              <h2 className="mt-2 font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">
                Every regulation that touches a practice — mapped to product controls.
              </h2>
            </div>
            <div className="space-y-3">
              {[
                'Migration Agents Code of Conduct Regulations 2021',
                'OMARA Sanction Schedule',
                'Australian Privacy Act 1988 + APPs',
                'Notifiable Data Breaches scheme',
                'NSW Law Society Trust Account Regulations',
                'AUSTRAC AML/CTF (Tranche-2 ready)',
                'Electronic Transactions Act 1999',
              ].map((r) => (
                <div key={r} className="flex items-start gap-2 text-sm text-slate-700">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#2ECC9A]" />
                  {r}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
